import type {
  Action,
  Capture,
  ContextRecord,
  CoreReference,
  Plan,
  ReviewItem,
  Signal,
} from '../types/core'
import { auth } from '../utils/firebase'
import { coreRepositories } from './coreDataService'

export type SignalsWorkspace = {
  signals: Signal[]
  plans: Plan[]
  actions: Action[]
  contextRecords: ContextRecord[]
  captures: Capture[]
  reviewItems: ReviewItem[]
}

export type CoreEvidenceItem = {
  ref: CoreReference
  label: string
  description?: string
  route?: string
}

export type SignalEvidenceItem = CoreEvidenceItem

let evaluationRequest: Promise<void> | null = null
let pendingEvaluationTimezone: string | null = null

const isOpen = (signal: Signal) => signal.status === 'active' || signal.status === 'seen'

const isExpired = (signal: Signal, now = Date.now()) => (
  Boolean(signal.expiresAt) && Date.parse(signal.expiresAt as string) <= now
)

const byPriority = (left: Signal, right: Signal) => (
  right.score.total - left.score.total ||
  right.score.urgency - left.score.urgency ||
  Date.parse(right.surfacedAt) - Date.parse(left.surfacedAt)
)

const getPlanRoute = (planId?: string) => planId ? `/plans/${planId}` : '/plans'

export const selectOpenSignals = (signals: Signal[]) => signals
  .filter((signal) => isOpen(signal) && !isExpired(signal))
  .sort(byPriority)

export const selectSignalHistory = (signals: Signal[]) => [...signals].sort((left, right) =>
  Date.parse(right.surfacedAt) - Date.parse(left.surfacedAt))

const isSignalRelevant = (signal: Signal, workspace: SignalsWorkspace) => {
  const plans = new Map(workspace.plans.map((plan) => [plan.id, plan]))
  const actions = new Map(workspace.actions.map((action) => [action.id, action]))
  const contextRecords = new Map(workspace.contextRecords.map((record) => [record.id, record]))
  const reviewItems = new Map(workspace.reviewItems.map((record) => [record.id, record]))
  return !signal.relatedRefs.some((reference) => {
    if (reference.kind === 'plan') {
      const plan = plans.get(reference.id)
      return !plan || plan.status === 'completed' || plan.status === 'archived'
    }
    if (reference.kind === 'action') {
      const action = actions.get(reference.id)
      return !action || action.status === 'completed' || action.status === 'dismissed'
    }
    if (reference.kind === 'context') {
      const context = contextRecords.get(reference.id)
      return !context || context.status === 'archived'
    }
    if (reference.kind === 'review') {
      const review = reviewItems.get(reference.id)
      return !review || review.status !== 'pending'
    }
    return false
  })
}

export const selectRelevantOpenSignals = (workspace: SignalsWorkspace) =>
  selectOpenSignals(workspace.signals).filter((signal) => isSignalRelevant(signal, workspace))

export const selectRelatedSignals = (
  workspace: SignalsWorkspace,
  kind: CoreReference['kind'],
  id: string,
) => selectRelevantOpenSignals(workspace).filter((signal) =>
  signal.relatedRefs.some((reference) => reference.kind === kind && reference.id === id))

export const selectTodaySignal = (workspace: SignalsWorkspace): Signal | null =>
  selectRelevantOpenSignals(workspace)[0] ?? null

export const resolveCoreEvidence = (
  references: CoreReference[],
  workspace: SignalsWorkspace,
): CoreEvidenceItem[] => {
  const plans = new Map(workspace.plans.map((record) => [record.id, record]))
  const actions = new Map(workspace.actions.map((record) => [record.id, record]))
  const contextRecords = new Map(workspace.contextRecords.map((record) => [record.id, record]))
  const captures = new Map(workspace.captures.map((record) => [record.id, record]))
  const reviewItems = new Map(workspace.reviewItems.map((record) => [record.id, record]))

  return references.map((reference) => {
    if (reference.kind === 'plan') {
      const record = plans.get(reference.id)
      return { ref: reference, label: record?.title ?? 'Plan', description: record?.purpose, route: getPlanRoute(reference.id) }
    }
    if (reference.kind === 'action') {
      const record = actions.get(reference.id)
      return { ref: reference, label: record?.title ?? 'Action', description: record?.details, route: getPlanRoute(record?.planId) }
    }
    if (reference.kind === 'context') {
      const record = contextRecords.get(reference.id)
      return { ref: reference, label: record?.title ?? 'Memory', description: record?.summary, route: '/remember' }
    }
    if (reference.kind === 'capture') {
      const record = captures.get(reference.id)
      return { ref: reference, label: record?.primaryText ?? 'Capture', route: '/' }
    }
    if (reference.kind === 'review') {
      const record = reviewItems.get(reference.id)
      return { ref: reference, label: record?.title ?? 'Review', route: '/' }
    }
    return { ref: reference, label: 'Tydy activity' }
  })
}

export const resolveSignalEvidence = (
  signal: Signal,
  workspace: SignalsWorkspace,
): SignalEvidenceItem[] => resolveCoreEvidence(signal.evidenceRefs, workspace)

export const routeForSignal = (signal: Signal, workspace: SignalsWorkspace): string => {
  const planRef = signal.relatedRefs.find((reference) => reference.kind === 'plan')
  if (planRef) return getPlanRoute(planRef.id)
  const actionRef = signal.relatedRefs.find((reference) => reference.kind === 'action')
  if (actionRef) return getPlanRoute(workspace.actions.find((action) => action.id === actionRef.id)?.planId)
  if (signal.relatedRefs.some((reference) => reference.kind === 'context')) return '/remember'
  return '/signals'
}

const updateStatus = async (
  userId: string,
  signalId: string,
  status: Signal['status'],
): Promise<Signal> => {
  const signal = await coreRepositories.signals.get(signalId)
  if (!signal || signal.userId !== userId) throw new Error('Signal was not found.')
  const now = new Date().toISOString()
  const next: Signal = { ...signal, status, updatedAt: now }

  if (status === 'seen') next.seenAt = signal.seenAt ?? now
  if (status === 'dismissed' || status === 'acted' || status === 'expired') next.resolvedAt = now
  if (status === 'active') {
    delete next.resolvedAt
  }

  return coreRepositories.signals.update(next)
}

export const coreSignalsService = {
  async loadWorkspace(userId: string): Promise<SignalsWorkspace> {
    const [signals, plans, actions, contextRecords, captures, reviewItems] = await Promise.all([
      coreRepositories.signals.listForUser(userId, 200),
      coreRepositories.plans.listForUser(userId, 200),
      coreRepositories.actions.listForUser(userId, 500),
      coreRepositories.contextRecords.listForUser(userId, 500),
      coreRepositories.captures.listForUser(userId, 500),
      coreRepositories.reviewItems.listForUser(userId, 200),
    ])
    return { signals, plans, actions, contextRecords, captures, reviewItems }
  },

  markSeen: (userId: string, signalId: string) => updateStatus(userId, signalId, 'seen'),
  dismiss: (userId: string, signalId: string) => updateStatus(userId, signalId, 'dismissed'),
  markActed: (userId: string, signalId: string) => updateStatus(userId, signalId, 'acted'),
  restore: (userId: string, signalId: string) => updateStatus(userId, signalId, 'active'),

  queueEvaluation(timezone = Intl.DateTimeFormat().resolvedOptions().timeZone): void {
    const user = auth.currentUser
    if (!user) return
    if (evaluationRequest) {
      pendingEvaluationTimezone = timezone
      return
    }

    evaluationRequest = user.getIdToken()
      .then(async (token) => {
        const response = await fetch('/api/signals/evaluate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ timezone }),
        })
        if (!response.ok) throw new Error(`Signal evaluation failed with ${response.status}.`)
      })
      .then(() => undefined)
      .catch((error) => console.warn('[Signals] Quiet evaluation failed.', error))
      .finally(() => {
        evaluationRequest = null
        const pendingTimezone = pendingEvaluationTimezone
        pendingEvaluationTimezone = null
        if (pendingTimezone) coreSignalsService.queueEvaluation(pendingTimezone)
      })
  },
}
